// THE REPO-ONBOARDING BLOCKER VOCABULARY — what a `blocked` setup pass may name as
// its prerequisite, and WHO clears each one. Split out of
// `apps/runner/src/modes/setup/verdict.ts` by #4362.
//
// IT LIVES HERE BECAUSE IT CROSSES THE WIRE. `prerequisite` is a field of
// {@link OnboardingVerdictPayloadSchema} (`./onboarding.ts`), so the box mints the
// value and at least three consumers outside the box read it — the mothership's
// verdict ingest, the maintainer notification, and the task-failure budget the wire
// `code` picks. Apps never import apps, so while the vocabulary sat inside
// `apps/runner` every one of those consumers could only HAND-COPY it, and two already
// had: a prose list in `../../entities/task/failure-kind.ts` pinned by a file-reading
// guard, and a `Set` of the two box-cleared members in
// `apps/worker/src/notifications/onboarding-wait.ts` whose drift cost was written in a
// comment. A comment is not a guard, and two hand-copied gates whose agreement is
// untested is the defect class #4352 was blocked on.
//
// WHAT IS NOT MOVED, ON PURPOSE: the WIRE stays free text bounded at 64
// (`OnboardingVerdictPayloadSchema.prerequisite`). Retyping it as `z.enum(SETUP_BLOCKERS)`
// would make a mothership refuse the WHOLE verdict of any box running a build that
// minted a member this one has never heard of — and a mixed-version fleet is the normal
// state, not the exception (the same reasoning `proof` and `scorecard` are optional for).
// The closed vocabulary is what the BOX may produce; {@link onboardingBlockerClearedBy}
// is the total, fail-safe read of what arrives.

import { TaskFailureKind } from '../../entities/task/failure-kind';

/**
 * A prerequisite a repo-onboarding pass cannot satisfy itself. Each names ONE thing
 * that happens next — a person acting, or the box repairing its own disk — and that is
 * the bar for a member here; there is no `unknown` arm, because a blocker nobody and
 * nothing can act on is a failure, and failures throw.
 *
 * An ARRAY, not a bare union, so the vocabulary is enumerable at runtime: a matrix test
 * iterates it, and {@link ONBOARDING_BLOCKER_CLEARED_BY} is keyed by it so a member
 * added here does not compile until the table answers for it.
 */
export const SETUP_BLOCKERS = [
  /** The box could not put the repo on disk (no token / clone refused / an operator-pinned path). */
  'no_checkout',
  /** The remote names no default branch and none is pinned — we never guess `main`. */
  'no_base_branch',
  /** git/gh holds no usable credential, so nothing can be pushed or opened. */
  'no_credential',
  /**
   * The assignment carries no sealed LLM credential, so there is no model for the
   * onboarding agent to run on (arc 101 slice 03) — the account connects a provider.
   * Refused before a byte is written: the one-call brain this agent replaced fell back
   * to `TODO(setup)` stubs here and reported them as a base, which is how 4 of 7
   * production trios shipped as placeholders. A different fact from `no_credential`
   * (git's) with a different remedy, so it is its own member.
   */
  'no_model_credential',
  /** The tree carries tracked modifications belonging to OTHER work — landing would fold them in. */
  'dirty_worktree',
  /**
   * An untracked file on the box's checkout stands where the branch this pass lands on
   * carries a path, so `git checkout` would ABORT (#3644). Its own member because
   * `github_refused` — where it used to land — asserts a cause that did not happen:
   * GitHub was never reached, and an operator reading that goes looking at the
   * repository for a problem that is on the box's disk.
   */
  'untracked_collision',
  /**
   * The setup lane's `dz/onboarding` checkout collided with files a PRIOR onboarding
   * run left on the box (#5570): the colliding paths are the platform-owned set
   * (`.dz/maintainer/`, `.githooks/`, `AGENTS.md`, `bin/check`, …), not the box's
   * own scratch — so the cause is a stale remote `dz/onboarding` branch from an
   * earlier pass that was never merged, every retry lands on the same wall, and the
   * operator's remedy is to close the open onboarding PR and delete the branch
   * (or merge it). Distinct from `untracked_collision` so the read is `human`, not
   * `box` — every retry fails identically and the box cannot heal its own disk.
   */
  'stale_onboarding_branch',
  /** GitHub refused the push or the pull request, and no onboarding PR exists afterwards. */
  'github_refused',
  /**
   * The repository's OWN commit hook refused the onboarding commit (#6528) — git itself
   * accepts it (its dry run passes, `team/git/hook-refusal.ts`), so nothing was pushed and
   * GitHub was never reached. It used to land as `github_refused`, carrying the first 400
   * chars of the hook's banner and never its verdict. The detail is the hook's output TAIL,
   * escapes stripped. `human`: the hook and the repo are the same on every box.
   */
  'commit_hook_refused',
  /**
   * The newest onboarding PR was closed unmerged, but the remote `dz/onboarding` no longer
   * holds that PR's head (#6527) — pushed to or recreated after the decline, or unreadable.
   * The landing replaces only the exact sha the owner declined, so it refuses rather than
   * adopt or overwrite somebody's commits. The detail names both shas and the PR; a person
   * deletes the branch, or opens a PR from it.
   */
  'onboarding_branch_moved',
  /**
   * The box cannot RUN the framework scaffolder the pass decided on (`bunx` is not on
   * its PATH / the spawn failed) — an operator puts bun on the box. Never a fallback
   * to detection (#3634): on a 0-file repo that would establish three `exit 1` stubs
   * as "the base" and report a success the maintainer reads as a scaffold.
   */
  'generator_unavailable',
  /**
   * The scaffolder ran and did not scaffold: a non-zero exit, or an exit 0 that left no
   * tree the pass can point at (`setup/executor/graft.ts` `graftScaffold` — nothing
   * in the staging directory, more than one tree in it, or one carrying its own `.git`).
   * Also the staging directory being somebody's checkout, which this pass will not
   * delete. The run's `generator` event carries the exit and the `fault`; re-run once
   * the cause (registry, network, the tool's own refusal, the directory) is cleared.
   */
  'generator_failed',
  /**
   * A GREENFIELD task named a stack with no house layout and no generator (#3756) —
   * the maintainer names a supported one (`setup/generator.ts` `unsupportedStackDetail`
   * lists them). It used to be a skip into detection, which on an empty tree wrote
   * three `exit 1` stubs and opened a pull request as if they were a base.
   */
  'unsupported_stack',
] as const;

/** One member of {@link SETUP_BLOCKERS}. */
export type SetupBlocker = (typeof SETUP_BLOCKERS)[number];

/**
 * Who has to do something before this repo's onboarding can finish: a person, or
 * nobody (#4362).
 *
 * `human` the blocker is a fact about the ACCOUNT or the REPOSITORY — a credential
 *         nobody minted, a remote that names no default branch, an App missing a
 *         permission, a stack with no generator. Identical on every box in the fleet,
 *         and nothing but a person changes it.
 * `box`   the blocker is a fact about ONE BOX'S DISK. No other box has it, and this
 *         box repairs it itself on the next delivery.
 */
export type OnboardingBlockerClearedBy = 'human' | 'box';

/**
 * WHO CLEARS EACH BLOCKER — the ONE table, and the only place the question is answered
 * for the whole fleet (#4362).
 *
 * TOTAL over {@link SetupBlocker} by its type, so a member added to the vocabulary
 * above does not compile until this table says which side of the line it falls on. Both
 * consumers are DERIVED from it and neither may restate it: the box grades its wire
 * `code` through {@link onboardingBlockerFailureKind}
 * (`apps/runner/src/modes/setup/executor/settle.ts`, `OnboardingBlockedError`), and the
 * mothership picks which sentence a maintainer reads through
 * {@link onboardingBlockerClearedBy} (`apps/worker/src/notifications/onboarding-wait.ts`).
 * Before this table each of those was hand-written against the other, agreement
 * asserted in a comment.
 *
 * THE LINE IS NOT "is it likely to pass next time". It is whether the fact the pass
 * refused on is per-BOX or per-ACCOUNT. A git token nobody minted, a repo with no
 * default branch, an App missing `workflows`, an unsupported stack, a box with no
 * `bunx`: identical on every box, and nothing but a human changes them — terminal, the
 * #3489 posture unchanged.
 *
 * A DIRTY TREE IS THE EXCEPTION AND ALWAYS WAS. `dirty_worktree` and
 * `untracked_collision` name one box's disk, and the pass REPAIRS IT ITSELF: the setup
 * executor's task-start tree preflight (`apps/runner/src/modes/setup/executor/run.ts`
 * step 0'', the coding lane's own `preflightTaskTree`) parks the dirt and lands the
 * tree on the fetched base before the redelivery writes a byte. So the redelivery heals
 * whether or not any other lane ever touches the box — which mattered, because on the
 * boxes this failure happened to (then pinned `setup`) no other lane could. Filing these two as human-owned made two production setup passes terminal at
 * zero attempts on 2026-09-17, both on trees the box itself would have cleaned.
 *
 * `github_refused` STAYS `human`, and that is the one worth stating: it is the
 * catch-all this lane files a genuinely unexplained landing failure under, so making it
 * box-cleared would hand the queue a kind that re-enters on any cause at all. What
 * #4362 moved out of it is the class that was never GitHub's — a local git abort — and
 * that moved by becoming TYPED (`apps/runner/src/modes/worker/team/git/collision.ts`),
 * not by widening this table.
 */
export const ONBOARDING_BLOCKER_CLEARED_BY: Readonly<
  Record<SetupBlocker, OnboardingBlockerClearedBy>
> = {
  no_checkout: 'human',
  no_base_branch: 'human',
  no_credential: 'human',
  no_model_credential: 'human',
  dirty_worktree: 'box',
  untracked_collision: 'box',
  stale_onboarding_branch: 'human',
  github_refused: 'human',
  commit_hook_refused: 'human',
  onboarding_branch_moved: 'human',
  generator_unavailable: 'human',
  generator_failed: 'human',
  unsupported_stack: 'human',
};

/**
 * Classify a `prerequisite` AS IT ARRIVED ON THE WIRE — free text, bounded at 64,
 * minted by a box whose build may be older or newer than this one's.
 *
 * TOTAL over any string, and an UNRECOGNIZED or absent prerequisite answers `human`.
 * That is the fail-safe side and it is the status quo ante: it tells a maintainer to
 * look at a pass that may in fact heal itself, where the other default tells a
 * maintainer nothing is wrong about a pass that is stuck forever (#3766's silence).
 * Never a throw and never a `null` — a sink that refuses a verdict it does not fully
 * recognize drops the whole `onboarding_verdict` event, stamp and all.
 */
export function onboardingBlockerClearedBy(
  prerequisite: string | null | undefined,
): OnboardingBlockerClearedBy {
  if (prerequisite === null || prerequisite === undefined) return 'human';
  return CLEARED_BY_WIRE_NAME.get(prerequisite) ?? 'human';
}

/**
 * The same table, read by an UNTRUSTED string. A `Map` and not `record[wireString]`:
 * the wire may carry `constructor`, `toString` or `__proto__`, and a plain-object
 * lookup answers those with something that is not a member of this vocabulary at all.
 */
const CLEARED_BY_WIRE_NAME: ReadonlyMap<string, OnboardingBlockerClearedBy> = new Map(
  Object.entries(ONBOARDING_BLOCKER_CLEARED_BY),
);

/**
 * The `tasks.failure_kind` a blocker earns — the TYPED side, called by the box on the
 * `SetupBlocker` its own verdict named, never on a wire string.
 *
 * Derived from {@link ONBOARDING_BLOCKER_CLEARED_BY} rather than tabled again, so the
 * budget and the maintainer's sentence cannot disagree about one blocker. The budgets
 * themselves are argued in {@link TaskFailureKind.OnboardingBlocked} and
 * {@link TaskFailureKind.OnboardingTreeDirty}.
 */
export function onboardingBlockerFailureKind(blocker: SetupBlocker): TaskFailureKind {
  return ONBOARDING_BLOCKER_CLEARED_BY[blocker] === 'box'
    ? TaskFailureKind.OnboardingTreeDirty
    : TaskFailureKind.OnboardingBlocked;
}
